import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CalendarView } from '@/components/calendar/CalendarView';
import { EventForm } from '@/components/calendar/EventForm';
import { useEvents, type Event } from '@/hooks/useEvents';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmDialog } from '@/components/ui/delete-confirm-dialog';
import { MoreHorizontal, Edit, Trash2, Clock, MapPin, User, Building } from 'lucide-react';
import { format } from 'date-fns';

export default function Calendar() {
  const { events, loading, createEvent, updateEvent, deleteEvent } = useEvents();
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handleCreateEvent = (date?: Date, startTime?: Date, endTime?: Date) => {
    setSelectedEvent(null);
    
    // If we have specific start/end times (from time-based views), use those
    if (startTime && endTime) {
      const eventData = {
        start_time: startTime.toISOString().slice(0, 16),
        end_time: endTime.toISOString().slice(0, 16),
      };
      // Store these in a ref or state to pre-fill the form
      setSelectedDate(startTime);
    } else {
      setSelectedDate(date || null);
    }
    
    setShowEventForm(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setSelectedDate(null);
    setShowEventDetails(false);
    setShowEventForm(true);
  };

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      await deleteEvent(selectedEvent.id);
      setShowDeleteDialog(false);
      setShowEventDetails(false);
      setSelectedEvent(null);
    }
  };

  const handleFormSubmit = async (data: any) => {
    if (selectedEvent) {
      return await updateEvent(selectedEvent.id, data);
    } else {
      // If we have a selected date but no start_time in data, set default times
      if (selectedDate && !data.start_time) {
        const dateStr = selectedDate.toISOString().slice(0, 16);
        // If selectedDate has time info (from time-based views), use it
        if (selectedDate.getHours() > 0 || selectedDate.getMinutes() > 0) {
          data.start_time = dateStr;
          const endTime = new Date(selectedDate);
          endTime.setHours(endTime.getHours() + 1);
          data.end_time = endTime.toISOString().slice(0, 16);
        } else {
          // Otherwise use default 9am-10am
          data.start_time = `${dateStr.slice(0, 10)}T09:00`;
          data.end_time = `${dateStr.slice(0, 10)}T10:00`;
        }
      }
      return await createEvent(data);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-2">Loading calendar...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-muted-foreground">
            Manage your schedule and appointments
          </p>
        </div>

        <CalendarView
          events={events}
          onDateSelect={setSelectedDate}
          onEventSelect={handleEventSelect}
          onCreateEvent={handleCreateEvent}
          onUpdateEvent={updateEvent}
        />

        {/* Event Form Dialog */}
        <Dialog open={showEventForm} onOpenChange={setShowEventForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedEvent ? 'Edit Event' : 'Create New Event'}
              </DialogTitle>
            </DialogHeader>
            <EventForm
              event={selectedEvent || undefined}
              onSubmit={handleFormSubmit}
              onCancel={() => setShowEventForm(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Event Details Dialog */}
        <Dialog open={showEventDetails} onOpenChange={setShowEventDetails}>
          <DialogContent className="max-w-2xl">
            {selectedEvent && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-xl">{selectedEvent.title}</DialogTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditEvent(selectedEvent)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Event
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setShowDeleteDialog(true)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Event
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {selectedEvent.description && (
                    <p className="text-muted-foreground">{selectedEvent.description}</p>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Time</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(selectedEvent.start_time), 'MMM dd, yyyy HH:mm')} - 
                          {format(new Date(selectedEvent.end_time), 'HH:mm')}
                        </p>
                      </div>
                    </div>

                    {selectedEvent.location && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">Location</p>
                          <p className="text-sm text-muted-foreground">{selectedEvent.location}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Type</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {selectedEvent.event_type.replace('_', ' ')}
                        </p>
                      </div>
                    </div>

                    {(selectedEvent.lead_id || selectedEvent.project_id) && (
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">Related</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedEvent.lead_id ? 'Lead' : 'Project'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDeleteEvent}
          title="Delete Event"
          description="Are you sure you want to delete this event? This action cannot be undone."
        />
      </div>
    </DashboardLayout>
  );
}